import { GridCellLayoutVariant } from "@/constants/gridCellLayouts";
import type { GenericLayout, GridCellLayout } from "@mm/metx-workbench.meteomatics.com";

export type LayoutViewport = {
  /**
   * The number of rows in the first level.
   */
  numRows: number;

  /**
   * The number of columns in the first level.
   */
  numColumns: number;

  /**
   * Indicates whether the layout has sub-rows on the second level.
   * This is used to determine the distribution of grid cells that don't fit
   * within numRows * numColumns, placing them into sub-rows on the right side cell.
   */
  hasSubRows: boolean;
  gridCellLayouts: GridCellLayout[];
};

export function areGridCellLayoutsEqual(gridCellLayout1: GridCellLayout, gridCellLayout2: GridCellLayout): boolean {
  return (
    gridCellLayout1.gridRowStart === gridCellLayout2.gridRowStart &&
    gridCellLayout1.gridRowEnd === gridCellLayout2.gridRowEnd &&
    gridCellLayout1.gridColumnStart === gridCellLayout2.gridColumnStart &&
    gridCellLayout1.gridColumnEnd === gridCellLayout2.gridColumnEnd
  );
}

/**
 * A comparator function to sort tools (maps, plots, etc) by the grid position
 * The order starts from the top-left grid, goes to the right of the same row,
 * and ends at the bottom-right grid.
 * e.g:
 * |  1  |  2  |  3  |
 * |  4  |  5  |  6  |
 */

export function compareGridCellLayouts(gridCellLayout1: GridCellLayout, gridCellLayout2: GridCellLayout) {
  if (gridCellLayout1.gridRowStart < gridCellLayout2.gridRowStart) {
    return -1;
  }
  if (gridCellLayout1.gridRowStart > gridCellLayout2.gridRowStart) {
    return 1;
  }
  if (gridCellLayout1.gridColumnStart < gridCellLayout2.gridColumnStart) {
    return -1;
  }
  if (gridCellLayout1.gridColumnStart > gridCellLayout2.gridColumnStart) {
    return 1;
  }
  return 0;
}

/**
 * In most cases we check only if the first cell is right.
 * This function is also only to find the right selected option
 * */
export function getLayoutFormatByLayouts(layouts: GenericLayout[]): GridCellLayoutVariant | undefined {
  const sortedLayouts = layouts.map((layout) => layout.gridCellLayout).sort((a, b) => compareGridCellLayouts(a, b));
  if (sortedLayouts.length <= 1) {
    return GridCellLayoutVariant.R1C1;
  }
  if (sortedLayouts.length === 6) {
    if (sortedLayouts[0].gridColumnEnd === 33) {
      return GridCellLayoutVariant.R2C3;
    }
  }
  if (sortedLayouts.length >= 4) {
    if (
      sortedLayouts[0].gridRowStart === 1 &&
      sortedLayouts[0].gridRowEnd === 97 &&
      sortedLayouts[0].gridColumnEnd === 49
    ) {
      // one column and second with 3 rows
      return GridCellLayoutVariant.R1C2_3SUB_ROW_LEFT;
    }

    return GridCellLayoutVariant.R2C2;
  }

  if (sortedLayouts.length === 2) {
    if (sortedLayouts[0].gridColumnEnd === 49) {
      return GridCellLayoutVariant.R1C2;
    }
    return GridCellLayoutVariant.R2C1;
  }
  if (sortedLayouts.length === 3) {
    if (
      sortedLayouts[0].gridRowStart === 1 &&
      sortedLayouts[0].gridRowEnd === 97 &&
      sortedLayouts[0].gridColumnEnd === 49
    ) {
      // one column and second with 2 rows
      return GridCellLayoutVariant.R1C2_2SUB_ROW_LEFT;
    }

    if (sortedLayouts[0].gridColumnEnd === 33) {
      return GridCellLayoutVariant.R1C3;
    }
    return GridCellLayoutVariant.R3C1;
  }

  // Some other layoutVariant
  return undefined;
}
